//src/pages/TenantDashboardPage.tsx
import type React from "react";
import { useEffect, useState } from "react";
import { Building2, CreditCard, LayoutDashboard, Loader2, Plus, Users } from "lucide-react";
import { useNavigate } from "react-router-dom";

import { PageHeader } from "@/components/shared/PageHeader";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { useAuth } from "@/contexts/AuthContext";
import { api } from "@/lib/apiClient";
import { PERMISSIONS } from "@/lib/permissions";

type TenantClientSummary = {
  total_clients: number;
  active_clients: number;
};

function StatCard({
  title,
  value,
  description,
  icon,
}: {
  title: string;
  value: string | number;
  description: string;
  icon: React.ReactNode;
}) {
  return (
    <Card className="border-border/70 shadow-sm">
      <CardContent className="flex items-start justify-between gap-4 p-5">
        <div>
          <p className="text-xs font-medium uppercase tracking-[0.14em] text-muted-foreground">{title}</p>
          <p className="mt-3 text-3xl font-bold tracking-tight">{value}</p>
          <p className="mt-1 text-sm text-muted-foreground">{description}</p>
        </div>
        <div className="rounded-2xl border border-border/70 bg-muted/50 p-3 text-muted-foreground">
          {icon}
        </div>
      </CardContent>
    </Card>
  );
}

export default function TenantDashboardPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [summary, setSummary] = useState<TenantClientSummary>({ total_clients: 0, active_clients: 0 });
  const [hasLoadedSummary, setHasLoadedSummary] = useState(false);
  const [isLoadingSummary, setIsLoadingSummary] = useState(true);
  const [loadError, setLoadError] = useState(false);

  const permissions = user?.permissions ?? [];
  const canReadClients = permissions.includes(PERMISSIONS.TENANT_CLIENTS_READ);
  const canWriteClients = permissions.includes(PERMISSIONS.TENANT_CLIENTS_WRITE);
  const canReadUsers = permissions.includes(PERMISSIONS.TENANT_USERS_READ);
  const canReadModels = [
    PERMISSIONS.TENANT_MODELS_DEPARTMENTS_READ,
    PERMISSIONS.TENANT_MODELS_JOB_POSITIONS_READ,
    PERMISSIONS.TENANT_MODELS_QUESTIONNAIRES_READ,
    PERMISSIONS.TENANT_MODELS_GRO_READ,
  ].some((permission) => permissions.includes(permission));

  useEffect(() => {
    let mounted = true;

    async function loadTenantSummary() {
      setIsLoadingSummary(true);
      setLoadError(false);
      try {
        const data = await api.get<TenantClientSummary>("/client-companies/summary", {
          clientContext: false,
        });
        if (mounted) setSummary(data);
      } catch {
        if (mounted) setLoadError(true);
      } finally {
        if (mounted) {
          setHasLoadedSummary(true);
          setIsLoadingSummary(false);
        }
      }
    }

    loadTenantSummary();
    return () => {
      mounted = false;
    };
  }, []);

  const hasClients = hasLoadedSummary && summary.total_clients > 0;

  return (
    <div className="space-y-6">
      <PageHeader
        title={user?.tenant?.name ? `${user.tenant.name} — Dashboard da Empresa` : "Dashboard da Empresa"}
        description="Acompanhe os Clientes cadastrados e os indicadores administrativos da Empresa."
      >
        {hasClients && canReadClients ? (
          <Button onClick={() => navigate("/cadastros/clientes")}>Gerenciar Clientes</Button>
        ) : !hasClients && hasLoadedSummary && canWriteClients ? (
          <Button onClick={() => navigate("/cadastros/clientes/novo")}>
            <Plus className="mr-2 h-4 w-4" />
            Cadastrar primeiro Cliente
          </Button>
        ) : null}
      </PageHeader>

      {isLoadingSummary || !hasLoadedSummary ? (
        <Card className="border-dashed">
          <CardContent className="flex min-h-64 items-center justify-center gap-3 text-sm text-muted-foreground">
            <Loader2 className="h-5 w-5 animate-spin" />
            Carregando dados da Empresa...
          </CardContent>
        </Card>
      ) : loadError ? (
        <Card className="border-dashed">
          <CardContent className="flex min-h-64 items-center justify-center text-sm text-muted-foreground">
            Não foi possível carregar os dados do Dashboard da Empresa.
          </CardContent>
        </Card>
      ) : !hasClients ? (
        <Card className="overflow-hidden border-primary/20 bg-gradient-to-br from-primary/[0.06] via-background to-background shadow-sm">
          <CardContent className="flex min-h-[360px] flex-col items-center justify-center px-6 py-12 text-center">
            <div className="flex h-16 w-16 items-center justify-center rounded-3xl border border-primary/20 bg-primary/10 text-primary">
              <Building2 className="h-8 w-8" />
            </div>
            <h2 className="mt-6 text-2xl font-bold tracking-tight">Nenhum Cliente cadastrado na Empresa</h2>
            <p className="mt-2 max-w-xl text-sm leading-6 text-muted-foreground sm:text-base">
              A Empresa ainda não possui Clientes cadastrados na plataforma.
            </p>
            {canWriteClients ? (
              <Button className="mt-6" onClick={() => navigate("/cadastros/clientes/novo")}>
                <Plus className="mr-2 h-4 w-4" />
                Cadastrar primeiro Cliente
              </Button>
            ) : null}
          </CardContent>
        </Card>
      ) : (
        <>
          <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
            <StatCard
              title="Clientes ativos"
              value={summary.active_clients}
              description="Clientes ativos cadastrados na Empresa."
              icon={<Building2 className="h-5 w-5" />}
            />
            <StatCard
              title="Clientes cadastrados"
              value={summary.total_clients}
              description="Total de Clientes vinculados à Empresa."
              icon={<LayoutDashboard className="h-5 w-5" />}
            />
            <StatCard
              title="Usuário logado"
              value={user?.name ? user.name.split(" ")[0] : "-"}
              description="Usuário da Empresa."
              icon={<Users className="h-5 w-5" />}
            />
            <StatCard
              title="Plano e cobrança"
              value="--"
              description="Resumo financeiro será conectado ao plano."
              icon={<CreditCard className="h-5 w-5" />}
            />
          </div>

          <div className="grid gap-4 lg:grid-cols-2">
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Administração da Empresa</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3 text-sm text-muted-foreground">
                <p>
                  Os módulos administrativos disponíveis dependem das permissões do seu perfil na Empresa.
                </p>
                {(canReadClients || canReadUsers || canReadModels) ? (
                  <div className="flex flex-wrap gap-2 pt-2">
                    {canReadClients ? (
                      <Button variant="outline" onClick={() => navigate("/cadastros/clientes")}>Clientes</Button>
                    ) : null}
                    {canReadUsers ? (
                      <Button variant="outline" onClick={() => navigate("/cadastros/usuarios")}>Usuários e acessos</Button>
                    ) : null}
                    {canReadModels ? (
                      <Button variant="outline" onClick={() => navigate("/tenant/modelos")}>Modelos</Button>
                    ) : null}
                  </div>
                ) : null}
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Operação de Cliente</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3 text-sm text-muted-foreground">
                <p>
                  Os Clientes disponíveis para operação são definidos pela carteira vinculada ao seu usuário.
                </p>
                <p>
                  Selecione um Cliente no topo da plataforma para entrar no contexto operacional e acessar os módulos permitidos.
                </p>
              </CardContent>
            </Card>
          </div>
        </>
      )}
    </div>
  );
}
